import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import React, { useCallback } from 'react';
import type { ControlledDateTimePickerContextT } from '../../config/useControlledDateTimePicker.js';
import { ControlledDateTimePickerDatatestid } from '../../constants/index.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';
import { StyledMeridiemInput } from '../Styleds.js';

const validFirstDigits = ['a', 'A', 'p', 'P'];
const validSecondDigits = ['m', 'M'];
const isInvalidMeridiem = (meridiem: string) => {
  if (meridiem === '') return false;
  const [firstDigit, secondDigit] = meridiem.split('');
  const isValidFirstDigit = (meridiem.length < 1 && !firstDigit) || validFirstDigits.includes(firstDigit);
  const isValidSecondDigit = !secondDigit || validSecondDigits.includes(secondDigit);
  return meridiem.length > 2 || !isValidFirstDigit || !isValidSecondDigit;
};

interface MeridiemInputsPropsT {
  onChange: DSControlledDateTimePickerInternalsT.OnChangeFunc;
  onBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc;
  onFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc;
  onKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown;
  value: string;
  disabled: boolean;
  applyAriaDisabled?: boolean;
  contextRef: React.MutableRefObject<HTMLInputElement | null>;
  placeholder: string;
  isFocused: boolean;
  getProps: ControlledDateTimePickerContextT['getProps'];
  tabIndex?: TypescriptHelpersT.WCAGTabIndex;
  readOnly: boolean;
}
export const MeridiemInput = React.memo(
  ({
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    value,
    disabled,
    applyAriaDisabled,
    placeholder,
    contextRef,
    isFocused,
    getProps,
    tabIndex,
    readOnly,
  }: MeridiemInputsPropsT): JSX.Element => {
    const validateOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (readOnly || applyAriaDisabled || disabled || isInvalidMeridiem(e?.target?.value)) {
          e.preventDefault();
          return;
        }
        onChange(e);
      },
      [applyAriaDisabled, disabled, onChange, readOnly],
    );
    return (
      <StyledMeridiemInput
        onChange={validateOnChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        value={value}
        innerRef={(DomElem: HTMLInputElement) => {
          contextRef.current = DomElem;
          if (isFocused) DomElem?.focus?.();
        }}
        tabIndex={tabIndex}
        data-testid={ControlledDateTimePickerDatatestid.TIME_INPUTS.MERIDIEM}
        placeholder={placeholder}
        aria-label="meridiem"
        disabled={disabled}
        aria-disabled={applyAriaDisabled}
        getOwnerProps={getProps}
        readOnly={readOnly}
      />
    );
  },
);
